/**
 * DotSphereSpinner — branded "AI thinking" spinner for VaultSentinel.
 *
 * Single-file React component. No deps beyond React. Drop into the project,
 * import the logo as you import any other static asset, and use it like:
 *
 *   import logoUrl from './assets/vsl-logo.png';
 *   import { DotSphereSpinner } from './DotSphereSpinner';
 *
 *   <DotSphereSpinner size={64} logoSrc={logoUrl} variant="dark" />
 *
 * Defaults are tuned to the look approved in design review. Override any of
 * the SpinnerTweaks props per-instance if you need to.
 *
 * Accessibility:
 *   - Renders inside a role="status" region with aria-live="polite".
 *   - `label` is announced by screen readers (defaults to "Loading").
 *   - Respects prefers-reduced-motion: the sphere stops rotating and the
 *     pulse becomes a gentle opacity breath instead of a sweeping band.
 *
 * Performance:
 *   - Pauses the animation when the canvas leaves the viewport or the tab
 *     is hidden, so a backgrounded spinner doesn't burn cycles.
 *   - Caches per-dot logo color so the bitmap is sampled only on mount /
 *     when dots/logoScale change, not every frame.
 */

import * as React from 'react';

// ─── Public API ──────────────────────────────────────────────────────────

export interface DotSphereSpinnerProps {
  /** Pixel size of the (square) canvas. */
  size?: number;
  /** URL of the logo PNG to wrap on the sphere. Omit for an unbranded sphere. */
  logoSrc?: string;
  /** Tone tier the spinner sits on. */
  variant?: 'dark' | 'light';
  /** Accessible label announced via aria-live. */
  label?: string;
  /** Per-instance tweaks — override the defaults below. */
  tweaks?: Partial<SpinnerTweaks>;
  /** Optional CSS class for the wrapper. */
  className?: string;
  /** Optional inline style for the wrapper. */
  style?: React.CSSProperties;
}

export interface SpinnerTweaks {
  /** Lean in degrees: positive = pole leans right. */
  tilt: number;
  /** Camera pitch in degrees. */
  viewAngle: number;
  /** 1× ≈ one quiet rotation every ~15 s. */
  spinSpeed: number;
  /** Total dots on the sphere. 1500–2500 reads cleanly at any size. */
  dots: number;
  /** Multiplier on per-dot radius. */
  dotSize: number;
  /** Number of simultaneous pulse bands. 1 is restrained, 2 lively. */
  pulses: number;
  /** Seconds for one pulse band to sweep pole-to-pole. */
  pulsePeriod: number;
  /** Logo decal size relative to the sphere. */
  logoScale: number;
  /** Logo presence multiplier (alpha gain on the decal mask). */
  logoStrength: number;
}

export const DEFAULT_TWEAKS: SpinnerTweaks = {
  tilt: -18,
  viewAngle: -8,
  spinSpeed: 1.2,
  dots: 2000,
  dotSize: 1.5,
  pulses: 1,
  pulsePeriod: 2.6,
  logoScale: 1.42,
  logoStrength: 2.0,
};

// ─── Module-level sampler cache ──────────────────────────────────────────
// Multiple spinner instances pointing at the same logo URL share one
// rasterized sampler. Avoids re-decoding the PNG per instance and per mount.

type Sampler = (u: number, v: number) => { r: number; g: number; b: number; a: number };

const samplerCache = new Map<string, Promise<Sampler>>();

function getSampler(src: string, sz = 256): Promise<Sampler> {
  const cached = samplerCache.get(src);
  if (cached) return cached;
  const p = loadSampler(src, sz);
  samplerCache.set(src, p);
  return p;
}

async function loadSampler(src: string, sz: number): Promise<Sampler> {
  const img = new Image();
  img.src = src;
  await new Promise<void>((res, rej) => {
    img.onload = () => res();
    img.onerror = (e) => rej(e);
  });
  const cv = document.createElement('canvas');
  cv.width = sz;
  cv.height = sz;
  const cx = cv.getContext('2d')!;
  cx.imageSmoothingEnabled = true;
  const pad = 0.03;
  const inner = sz * (1 - pad * 2);
  const ar = img.width / img.height;
  let dw: number, dh: number;
  if (ar >= 1) {
    dw = inner;
    dh = inner / ar;
  } else {
    dh = inner;
    dw = inner * ar;
  }
  cx.drawImage(img, (sz - dw) / 2, (sz - dh) / 2, dw, dh);
  const data = cx.getImageData(0, 0, sz, sz).data;
  return (u, v) => {
    if (u < 0 || u > 1 || v < 0 || v > 1) return { r: 0, g: 0, b: 0, a: 0 };
    const x = Math.min(sz - 1, Math.floor(u * sz));
    const y = Math.min(sz - 1, Math.floor(v * sz));
    const p = (y * sz + x) * 4;
    return { r: data[p], g: data[p + 1], b: data[p + 2], a: data[p + 3] / 255 };
  };
}

// ─── Math helpers ────────────────────────────────────────────────────────

function fibSphere(n: number): Array<[number, number, number]> {
  const pts: Array<[number, number, number]> = new Array(n);
  const phi = Math.PI * (Math.sqrt(5) - 1);
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / Math.max(1, n - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const t = phi * i;
    pts[i] = [Math.cos(t) * r, y, Math.sin(t) * r];
  }
  return pts;
}

// ─── Component ───────────────────────────────────────────────────────────

export function DotSphereSpinner({
  size = 64,
  logoSrc,
  variant = 'dark',
  label = 'Loading',
  tweaks,
  className,
  style,
}: DotSphereSpinnerProps) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const samplerRef = React.useRef<Sampler | null>(null);
  // Latest tweaks live in a ref so the rAF loop reads them without restarts.
  const tweaksRef = React.useRef<SpinnerTweaks>({ ...DEFAULT_TWEAKS, ...tweaks });
  tweaksRef.current = { ...DEFAULT_TWEAKS, ...tweaks };

  const reducedMotion = useReducedMotion();
  const inView = useInView(canvasRef);

  // Load logo sampler once per src.
  React.useEffect(() => {
    if (!logoSrc) {
      samplerRef.current = null;
      return;
    }
    let cancelled = false;
    getSampler(logoSrc).then((s) => {
      if (!cancelled) samplerRef.current = s;
    }).catch(() => {
      // Logo unavailable — render unbranded sphere rather than crashing.
      if (!cancelled) samplerRef.current = null;
    });
    return () => { cancelled = true; };
  }, [logoSrc]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Hi-DPI fit.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Geometry + per-dot decal cache. Rebuilt on dots / logoScale change.
    let N = 0;
    let points: Array<[number, number, number]> = [];
    let proj: Array<[number, number, number]> = [];
    let decal: Array<[number, number, number, number]> = [];
    let lastDots = -1;
    let lastLogoScale = -1;

    function rebuild() {
      const tw = tweaksRef.current;
      const sampler = samplerRef.current;
      N = tw.dots;
      points = fibSphere(N);
      proj = new Array(N);
      decal = new Array(N);
      for (let i = 0; i < N; i++) {
        proj[i] = [0, 0, 0];
        const lp = points[i];
        if (sampler && lp[2] > 0.10) {
          const u = (lp[0] / tw.logoScale) + 0.5;
          const v = (lp[1] / tw.logoScale) + 0.5;
          const c = sampler(u, v);
          const faceFade = Math.min(1, (lp[2] - 0.10) / 0.30);
          decal[i] = [c.r, c.g, c.b, c.a * faceFade];
        } else {
          decal[i] = [0, 0, 0, 0];
        }
      }
      lastDots = tw.dots;
      lastLogoScale = tw.logoScale;
    }

    const dark = variant === 'dark';
    const baseRGB: [number, number, number] = dark ? [165, 185, 215] : [80, 110, 145];
    const hiRGB: [number, number, number]   = dark ? [205, 230, 255] : [40, 90, 170];

    let raf = 0;
    let running = false;
    const start = performance.now();
    let lastSamplerLoaded = false;

    function frame(now: number) {
      const tw = tweaksRef.current;

      // Rebuild if dots, logo scale, or the sampler just finished loading.
      const samplerReady = samplerRef.current !== null;
      if (
        tw.dots !== lastDots ||
        tw.logoScale !== lastLogoScale ||
        samplerReady !== lastSamplerLoaded
      ) {
        rebuild();
        lastSamplerLoaded = samplerReady;
      }

      // Time. If reduced motion is on, freeze rotation/pulse advancement at
      // t=0; we'll still draw the sphere (so the spinner is *present*), but
      // animate only a subtle opacity breath below.
      const tRaw = (now - start) / 1000;
      const t = reducedMotion ? 0 : tRaw;

      const w = size, h = size;
      ctx!.clearRect(0, 0, w, h);
      if (!N) {
        raf = requestAnimationFrame(frame);
        return;
      }

      const cx = w / 2, cy = h / 2;
      const R = Math.min(w, h) * 0.42;

      const ay = t * 0.42 * tw.spinSpeed;
      const ax = (tw.viewAngle * Math.PI / 180) + Math.sin(t * 0.25) * 0.04;
      const az = -(tw.tilt * Math.PI / 180);

      // Project + z-sort.
      const cosY = Math.cos(ay), sinY = Math.sin(ay);
      const cosX = Math.cos(ax), sinX = Math.sin(ax);
      const cosZ = Math.cos(az), sinZ = Math.sin(az);
      for (let i = 0; i < N; i++) {
        const lp = points[i];
        // rotY
        let x = cosY * lp[0] + sinY * lp[2];
        let y = lp[1];
        let z = -sinY * lp[0] + cosY * lp[2];
        // rotX
        const yx = cosX * y - sinX * z;
        const zx = sinX * y + cosX * z;
        y = yx; z = zx;
        // rotZ
        const xz = cosZ * x - sinZ * y;
        const yz = sinZ * x + cosZ * y;
        x = xz; y = yz;
        proj[i][0] = x; proj[i][1] = y; proj[i][2] = z;
      }
      const order = new Array<number>(N);
      for (let i = 0; i < N; i++) order[i] = i;
      order.sort((a, b) => proj[a][2] - proj[b][2]);

      // Pulse params.
      const PULSE_COUNT = Math.max(0, Math.round(tw.pulses));
      const PULSE_PERIOD = Math.max(0.4, tw.pulsePeriod);
      const pulsePhases = new Array<number>(PULSE_COUNT);
      for (let k = 0; k < PULSE_COUNT; k++) {
        const u = ((t / PULSE_PERIOD) + k / Math.max(1, PULSE_COUNT)) % 1;
        pulsePhases[k] = 1 - u * 2;
      }
      const sig2 = 0.18 * 0.18;

      // Proportional dot scale relative to a 320 px reference.
      const sizeFactor = Math.min(w, h) / 320;

      // Reduced-motion breath — a single soft pulse phase computed from
      // tRaw so the orb still looks alive even when rotation is frozen.
      const breath = reducedMotion ? 0.5 + 0.5 * Math.sin(tRaw * 1.4) : 1;

      for (let oi = 0; oi < N; oi++) {
        const i = order[oi];
        const x = proj[i][0], y = proj[i][1], z = proj[i][2];
        const lp = points[i];
        const depth = (z + 1) / 2;
        if (depth < 0.04) continue;

        let pulse = 0;
        for (let k = 0; k < PULSE_COUNT; k++) {
          const dy = lp[1] - pulsePhases[k];
          pulse += Math.exp(-(dy * dy) / sig2);
        }
        pulse = Math.min(1, pulse);

        const dR = decal[i][0], dG = decal[i][1], dB = decal[i][2], dA = decal[i][3];
        const logoMix = Math.min(1, dA * tw.logoStrength);

        const px = cx + x * R, py = cy + y * R;
        const depthPow = depth * depth;
        const r = (0.45 + depthPow * 1.9 + logoMix * 0.9 + pulse * 1.0) * tw.dotSize * sizeFactor;

        // Logo coloring: lift on dark, push toward brand blue on light.
        let sR: number, sG: number, sB: number;
        if (dark) {
          sR = Math.min(255, dR * 1.10 + 30);
          sG = Math.min(255, dG * 1.10 + 35);
          sB = Math.min(255, dB * 1.05 + 50);
        } else {
          const brand = [25, 95, 195];
          const mix = 0.55;
          sR = dR * (1 - mix) + brand[0] * mix;
          sG = dG * (1 - mix) + brand[1] * mix;
          sB = dB * (1 - mix) + brand[2] * mix;
        }

        let rC = baseRGB[0] + (sR - baseRGB[0]) * logoMix;
        let gC = baseRGB[1] + (sG - baseRGB[1]) * logoMix;
        let bC = baseRGB[2] + (sB - baseRGB[2]) * logoMix;

        if (pulse > 0.04) {
          const tgt = logoMix > 0.35
            ? hiRGB
            : (dark ? [200, 220, 245] : [170, 200, 235]);
          const mix = pulse * 0.65;
          rC = rC + (tgt[0] - rC) * mix;
          gC = gC + (tgt[1] - gC) * mix;
          bC = bC + (tgt[2] - bC) * mix;
        }

        // Halo on dark only (additive glow muddies light backgrounds).
        if (dark && logoMix > 0.35) {
          const ga = Math.min(0.55, (0.22 + pulse * 0.55) * (0.4 + depth));
          const g = ctx!.createRadialGradient(px, py, 0, px, py, r * 4.2);
          g.addColorStop(0, `rgba(${hiRGB[0]},${hiRGB[1]},${hiRGB[2]},${ga})`);
          g.addColorStop(1, `rgba(${hiRGB[0]},${hiRGB[1]},${hiRGB[2]},0)`);
          ctx!.fillStyle = g;
          ctx!.beginPath();
          ctx!.arc(px, py, r * 4.2, 0, Math.PI * 2);
          ctx!.fill();
        }

        const alphaBase = dark ? (0.04 + depthPow * 0.78) : (0.10 + depthPow * 0.85);
        const alpha = Math.min(1, (alphaBase + logoMix * 0.35 + pulse * 0.28) * breath);

        ctx!.fillStyle = `rgba(${Math.round(rC)},${Math.round(gC)},${Math.round(bC)},${alpha})`;
        ctx!.beginPath();
        ctx!.arc(px, py, r, 0, Math.PI * 2);
        ctx!.fill();
      }

      raf = requestAnimationFrame(frame);
    }

    function startLoop() {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(frame);
    }
    function stopLoop() {
      if (!running) return;
      running = false;
      cancelAnimationFrame(raf);
    }

    // Don't burn cycles when off-screen or tab is hidden.
    if (inView && !document.hidden) startLoop();

    function onVisibility() {
      if (document.hidden) stopLoop();
      else if (inView) startLoop();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stopLoop();
    };
    // size + variant + reducedMotion + inView change → restart effect with
    // fresh palette / canvas dimensions. Tweaks are read via ref so changes
    // there do NOT cause a restart.
  }, [size, variant, reducedMotion, inView]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={className}
      style={{
        display: 'inline-block',
        lineHeight: 0,
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{ width: size, height: size, display: 'block' }}
      />
      <span style={visuallyHidden}>{label}</span>
    </div>
  );
}

// ─── Small hooks ─────────────────────────────────────────────────────────

function useReducedMotion(): boolean {
  const [v, setV] = React.useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setV(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return v;
}

function useInView(ref: React.RefObject<Element | null>): boolean {
  const [inView, setInView] = React.useState(true);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInView(e.isIntersecting);
      },
      { rootMargin: '64px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

// ─── Styles ──────────────────────────────────────────────────────────────

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default DotSphereSpinner;
